"use client";

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useAuth } from "@/contexts/auth-context";
import { useBookCatalog } from "@/contexts/book-catalog-context";
import { loadBookLocation, saveBookLocation } from "@/lib/actions/book-location";
import { NO_BOOK_LOCATION, availability, type Availability, type BookLocation, type SavedBookLocation } from "@/lib/book-location";
import { LOCAL_BYPASS_USER_ID } from "@/lib/local-bypass-auth";
import type { UsState } from "@/lib/us-states";

export type BookAvailabilityValue = Availability & {
  readonly status: "loading" | "ready" | "failed";
  readonly retry: () => void;
  readonly setState: (state: UsState | null) => void;
  readonly setHereOnly: (on: boolean) => void;
};

type SaveQueue = { readonly owner: string; confirmed: BookLocation; next: BookLocation | null; busy: boolean };

const noop = () => {};

const BookAvailabilityContext = createContext<BookAvailabilityValue>({
  ...availability(null, NO_BOOK_LOCATION, null),
  status: "loading",
  retry: noop,
  setState: noop,
  setHereOnly: noop,
});

export function BookAvailabilityProvider({ userId, initialLocation, detected, children }: {
  userId: string | null;
  initialLocation: BookLocation | null;
  detected: UsState | null;
  children: ReactNode;
}) {
  const catalog = useBookCatalog();
  const { user, isLoading } = useAuth();
  const account = isLoading ? userId : (user?.id ?? null);
  const localOnly = account === null || account === LOCAL_BYPASS_USER_ID;
  const [row, setRow] = useState<SavedBookLocation>({ userId, location: initialLocation });
  const [failed, setFailed] = useState(false);
  const [failedAccount, setFailedAccount] = useState(account);
  if (failedAccount !== account) {
    setFailedAccount(account);
    setFailed(false);
  }
  const queueRef = useRef<SaveQueue | null>(null);
  const location = row.userId === account ? row.location : null;

  useEffect(() => {
    if (location !== null || failed) return;
    queueRef.current = null;
    if (localOnly) {
      setRow({ userId: account, location: NO_BOOK_LOCATION });
      return;
    }
    let live = true;
    const fail = () => { if (live) setFailed(true); };
    loadBookLocation().then((loaded) => {
      if (!live) return;
      if (loaded.userId === account && loaded.location !== null) setRow(loaded);
      else fail();
    }, fail);
    return () => { live = false; };
  }, [account, failed, localOnly, location]);

  const retry = useCallback(() => setFailed(false), []);

  const save = useCallback((patch: Partial<Pick<BookLocation, "state" | "hereOnly">>) => {
    if (location === null) return;
    const next = { ...location, ...patch };
    setRow({ userId: account, location: next });
    if (localOnly || account === null) return;
    const queue = queueRef.current?.owner === account
      ? queueRef.current
      : (queueRef.current = { owner: account, confirmed: location, next: null, busy: false });
    queue.next = next;
    if (queue.busy) return;
    queue.busy = true;
    void (async () => {
      while (queue.next !== null) {
        const sent = queue.next;
        queue.next = null;
        const saved = await saveBookLocation(queue.owner, sent).then((result) => result.success, () => false);
        if (queueRef.current !== queue) return;
        if (saved) queue.confirmed = sent;
        else if (queue.next === null) setRow({ userId: queue.owner, location: queue.confirmed });
      }
      queue.busy = false;
    })();
  }, [account, localOnly, location]);

  const value = useMemo<BookAvailabilityValue>(() => ({
    ...availability(catalog, location ?? NO_BOOK_LOCATION, detected),
    status: location !== null ? "ready" : failed ? "failed" : "loading",
    retry,
    setState: (state) => save({ state }),
    setHereOnly: (hereOnly) => save({ hereOnly }),
  }), [catalog, detected, failed, location, retry, save]);
  return <BookAvailabilityContext.Provider value={value}>{children}</BookAvailabilityContext.Provider>;
}

export function useBookAvailability(): BookAvailabilityValue {
  return useContext(BookAvailabilityContext);
}
